import type { TFunction } from "i18next";
import {
  isConfiguratorProduct,
  isKITKITProduct,
  type PackagingInformationEntry,
} from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type {
  PackagingOption,
  PackagingStockEntry,
  PackagingStockEntryCW,
} from "~/domains/product/types/PackagingOption";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { SalesStatus } from "~/features/productDetails/components/PhaseInOut/SalesStatus";
import { canSeeCountryOrigin } from "~/utils/siteModes";

export type SalePerPack = Record<
  string,
  {
    soldOut: boolean;
    salesStock: number;
  }
>;

/**
 * Converts a filtered list of PackagingInformationEntry into PackagingOption[].
 *
 * Caller is responsible for:
 *  - pre-filtering entries (restriction, campaign, etc.)
 *  - providing salePerPack AFTER useSalesData() has resolved
 *  - providing t() for warehouse name translation
 */
export function buildPackagingOptions(
  entries: PackagingInformationEntry[],
  descriptor: ProductDescriptor,
  salePerPack: SalePerPack,
  t: TFunction,
): PackagingOption[] {
  const isKitKit = isKITKITProduct(descriptor.base);

  return entries.map((entry) => {
    const product = descriptor.base;
    const fifteenDigitEntry = product.fifteenDigit?.[entry.packCode];
    const saleData = salePerPack[entry.packCode];

    let label: PackagingOption["label"];
    if (!product.quantityVisible) {
      label = "dts";
    } else if (isProductPMA(descriptor) || isConfiguratorProduct(product)) {
      label = "standard";
    } else {
      label = "packSize";
    }

    const saleStock: PackagingStockEntry[] = saleData
      ? [
          {
            quantity: saleData.salesStock,
            sale: true,
            ...(saleData.soldOut ? { soldOut: true } : {}),
          },
        ]
      : [];

    const saleStockWeekly: PackagingStockEntryCW[] = saleData
      ? [
          {
            quantity: saleData.salesStock,
            cwDate: "",
            sale: true,
            ...(saleData.soldOut ? { soldOut: true } : {}),
          },
        ]
      : [];

    return {
      packCode: entry.packCode,
      packSize: entry.packSize,
      label,
      ean: isKitKit ? null : fifteenDigitEntry?.schaefflerEan ? [fifteenDigitEntry.schaefflerEan] : [],
      aliases: isKitKit ? null : product.packageAliases?.[entry.packCode] ?? [],
      countriesOfOrigin: isKitKit
        ? null
        : !canSeeCountryOrigin()
        ? []
        : fifteenDigitEntry?.countryOfOrigin
        ? [fifteenDigitEntry.countryOfOrigin]
        : [],
      preferredPackQuantity: entry.preferredPackQuantity,
      isDiscontinued: fifteenDigitEntry?.salesStatus === SalesStatus.Discontinued,
      productDescriptor: descriptor,
      stockLevels: {
        replenishmentLeadTimeDate: entry.replenishmentLeadTimeDate,
        warehouseName: translateWarehouseName(t, entry.primaryWarehouseName),
        inStock: [...saleStock, ...entry.totalWarehouse.inStock],
        inStockWeekly: [...saleStockWeekly, ...entry.totalWarehouse.inStockWeekly],
        inProduction: entry.totalWarehouse.inProduction,
        inProductionWeekly: entry.totalWarehouse.inProductionWeekly,
        inClarification: [entry.totalWarehouse.inClarification],
      },
      predecessors: [],
    };
  });
}
